<script setup>
/**
 * FormControlMBasketMerchantdetailsSettings
 * Dynamically switches merchant credential input configurations based on the selected gateway.
 * Wipes out stale config on gateway mutation to enforce strict boundary safety.
 */
import {computed, watch, onMounted} from 'vue'
import {useFormStore} from '@/stores/form'

const props = defineProps({
    // Standard Orchestrator Props
    formId: {
        type: String,
        required: true
    },
    field: {
        type: Object,
        required: true
    },
    currentData: {
        type: Object,
        required: true
    },
    value: {
        type: [Object, String],
        default: null
    }
})

// Dynamic Pinia Store
const formStore = computed(() => props.formId ? useFormStore(props.formId) : null)

// --- Computed ---

/**
 * Accesses the selected gateway name directly from the dynamic orchestrator state.
 */
const gatewayName = computed(() => formStore.value?.currentData?.gatewayName)

// --- Watchers ---

/**
 * Defensive reset: If the user swaps gateways midway, ensure we flush
 * the previous gateway keys out of memory instantly.
 */
watch(gatewayName, () => {
    // Ensuring nested reactive properties remain clean
    props.currentData.settings = {}
})

// --- Methods ---

/**
 * Validation logic hook placeholder.
 * Retained for parity with your orchestrator validation layer.
 */
const isRequired = (name) => {
    return false
}

// --- Lifecycle ---

onMounted(() => {
    // Custom orchestrator layout override flag
    props.field.replaceFormgroup = true

    // Safety check if settings object is completely uninitialized at boot
    if (!props.currentData.settings) {
        props.currentData.settings = {}
    }
})
</script>

<template>
    <div style="margin-bottom: 40px">

        <div v-if="currentData.gatewayName === 'PayPal'" class="well well-sm border rounded p-3 bg-light">
            <div class="mb-2 row align-items-center">
                <label class="col-xl-4 form-label fw-semibold">PayPal ID or email address</label>
                <div class="col-xl-8 col-lg-9 control-wrapper">
                    <input
                        v-model="currentData.settings.payPalId"
                        class="form-control"
                        name="settingsPayPalId"
                    >
                </div>
            </div>

            <div class="mb-2 row align-items-center">
                <label class="col-xl-4 form-label fw-semibold">PayPal test client ID</label>
                <div class="col-xl-8 col-lg-9 control-wrapper">
                    <input
                        v-model="currentData.settings.payPalTestClientId"
                        class="form-control"
                        name="settingsPayPalTestClientId"
                    >
                </div>
            </div>

            <div class="mb-2 row align-items-center">
                <label class="col-xl-4 form-label fw-semibold">PayPal test secret</label>
                <div class="col-xl-8 col-lg-9 control-wrapper">
                    <input
                        v-model="currentData.settings.payPalTestSecret"
                        class="form-control"
                        name="settingsPayPalTestSecret"
                        type="password"
                        autocomplete="new-password"
                    >
                </div>
            </div>

            <div class="mb-2 row align-items-center">
                <label class="col-xl-4 form-label fw-semibold">PayPal client ID</label>
                <div class="col-xl-8 col-lg-9 control-wrapper">
                    <input
                        v-model="currentData.settings.payPalClientId"
                        class="form-control"
                        name="settingsPayPalClientId"
                    >
                </div>
            </div>

            <div class="mb-2 row align-items-center">
                <label class="col-xl-4 form-label fw-semibold">PayPal secret</label>
                <div class="col-xl-8 col-lg-9 control-wrapper">
                    <input
                        v-model="currentData.settings.payPalSecret"
                        class="form-control"
                        name="settingsPayPalSecret"
                        type="password"
                        autocomplete="new-password"
                    >
                </div>
            </div>
        </div>

        <div v-else-if="currentData.gatewayName === 'Pay360'" class="well well-sm border rounded p-3 bg-light">
            <div class="mb-2 row align-items-center">
                <label class="col-xl-4 form-label fw-semibold">API username</label>
                <div class="col-xl-8 col-lg-9 control-wrapper">
                    <input
                        v-model="currentData.settings.apiUsername"
                        :class="{ 'is-invalid': isRequired('settingsApiUsername') }"
                        class="form-control"
                        name="settingsApiUsername"
                        required
                    >
                    <span class="form-text text-danger small">Required.</span>
                </div>
            </div>

            <div class="mb-2 row align-items-center">
                <label class="col-xl-4 form-label fw-semibold">API password</label>
                <div class="col-xl-8 col-lg-9 control-wrapper">
                    <input
                        v-model="currentData.settings.apiPassword"
                        :class="{ 'is-invalid': isRequired('settingsApiPassword') }"
                        class="form-control"
                        name="settingsApiPassword"
                        required
                        type="password"
                        autocomplete="new-password"
                    >
                    <span class="form-text text-danger small">Required.</span>
                </div>
            </div>

            <div class="mb-2 row align-items-center">
                <label class="col-xl-4 form-label fw-semibold">Hosted cashier installation ID</label>
                <div class="col-xl-8 col-lg-9 control-wrapper">
                    <input
                        v-model="currentData.settings.installationId"
                        :class="{ 'is-invalid': isRequired('settingsInstallationId') }"
                        class="form-control"
                        name="settingsInstallationId"
                        required
                    >
                    <span class="form-text text-danger small">Required.</span>
                </div>
            </div>

            <div class="mb-2 row align-items-center">
                <label class="col-xl-4 form-label fw-semibold">Sandbox API username</label>
                <div class="col-xl-8 col-lg-9 control-wrapper">
                    <input
                        v-model="currentData.settings.sandboxApiUsername"
                        :class="{ 'is-invalid': isRequired('settingsSandboxApiUsername') }"
                        class="form-control"
                        name="settingsSandboxApiUsername"
                    >
                </div>
            </div>

            <div class="mb-2 row align-items-center">
                <label class="col-xl-4 form-label fw-semibold">Sandbox API password</label>
                <div class="col-xl-8 col-lg-9 control-wrapper">
                    <input
                        v-model="currentData.settings.sandboxApiPassword"
                        :class="{ 'is-invalid': isRequired('settingsSandboxApiPassword') }"
                        class="form-control"
                        name="settingsSandboxApiPassword"
                        type="password"
                        autocomplete="new-password"
                    >
                </div>
            </div>

            <div class="mb-2 row align-items-center">
                <label class="col-xl-4 form-label fw-semibold">Sandbox hosted cashier installation ID</label>
                <div class="col-xl-8 col-lg-9 control-wrapper">
                    <input
                        v-model="currentData.settings.sandboxInstallationId"
                        :class="{ 'is-invalid': isRequired('settingsSandboxInstallationId') }"
                        class="form-control"
                        name="settingsSandboxInstallationId"
                    >
                    <span class="form-text text-danger small">Required.</span>
                </div>
            </div>
        </div>

        <div v-else-if="currentData.gatewayName === 'SecPay'" class="well well-sm border rounded p-3 bg-light">
            <div class="mb-2 row align-items-center">
                <label class="col-xl-4 form-label fw-semibold">Merchant ID</label>
                <div class="col-xl-8 col-lg-9 control-wrapper">
                    <input
                        v-model="currentData.settings.merchantId"
                        :class="{ 'is-invalid': isRequired('settingsMerchantId') }"
                        class="form-control"
                        name="settingsMerchantId"
                        required
                    >
                    <span class="form-text text-danger small">Required.</span>
                </div>
            </div>

            <div class="mb-2 row align-items-center">
                <label class="col-xl-4 form-label fw-semibold">Digest key</label>
                <div class="col-xl-8 col-lg-9 control-wrapper">
                    <input
                        v-model="currentData.settings.digestKey"
                        :class="{ 'is-invalid': isRequired('settingsDigestKey') }"
                        class="form-control"
                        name="settingsDigestKey"
                        required
                    >
                    <span class="form-text text-danger small">Required.</span>
                </div>
            </div>
        </div>

        <div v-else-if="currentData.gatewayName === 'Stripe'" class="well well-sm border rounded p-3 bg-light">
            <div class="mb-2 row align-items-center">
                <label class="col-xl-4 form-label fw-semibold">Secret key</label>
                <div class="col-xl-8 col-lg-9 control-wrapper">
                    <input
                        v-model="currentData.settings.secretKey"
                        :class="{ 'is-invalid': isRequired('settingsSecretKey') }"
                        class="form-control"
                        name="settingsSecretKey"
                        required
                    >
                    <span class="form-text text-danger small">Required.</span>
                </div>
            </div>

            <div class="mb-2 row align-items-center">
                <label class="col-xl-4 form-label fw-semibold">Publishable key</label>
                <div class="col-xl-8 col-lg-9 control-wrapper">
                    <input
                        v-model="currentData.settings.publishableKey"
                        :class="{ 'is-invalid': isRequired('settingsPublishableKey') }"
                        class="form-control"
                        name="settingsPublishableKey"
                        required
                    >
                    <span class="form-text text-danger small">Required.</span>
                </div>
            </div>

            <div class="mb-2 row align-items-center">
                <label class="col-xl-4 form-label fw-semibold">Test secret key</label>
                <div class="col-xl-8 col-lg-9 control-wrapper">
                    <input
                        v-model="currentData.settings.testSecretKey"
                        class="form-control"
                        name="settingsTestSecretKey"
                        required
                    >
                </div>
            </div>

            <div class="mb-2 row align-items-center">
                <label class="col-xl-4 form-label fw-semibold">Test publishable key</label>
                <div class="col-xl-8 col-lg-9 control-wrapper">
                    <input
                        v-model="currentData.settings.testPublishableKey"
                        class="form-control"
                        name="settingsTestPublishableKey"
                        required
                    >
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
.well {
    overflow: hidden;
    box-shadow: inset 0 1px 1px rgba(0, 0, 0, .05);
}
</style>